import React from 'react';
import type { EvaluationItem, FreeTextEssayConfig } from '../../../../../types/course';
import FloatingLabelPill from '../shared/FloatingLabelPill';
import SectionHeader from '../shared/SectionHeader';
import TrashButton from '../shared/TrashButton';

interface ItemEditorProps {
  item: Extract<EvaluationItem, { type: 'free_text_essay' }>;
  onChange: (patch: Partial<Extract<EvaluationItem, { type: 'free_text_essay' }>>) => void;
  onDelete: () => void;
  readOnly?: boolean;
}

const numberInputClass =
  'h-10 px-3 rounded-full border border-text-secondary bg-transparent text-text-primary font-sans text-sm outline-none focus:border-primary-500 transition-colors';

export default function FreeTextEssayItem({ item, onChange, onDelete, readOnly }: ItemEditorProps) {
  const { prompt, sample_answer, min_words, max_words } = item.config;

  const updateConfig = (patch: Partial<FreeTextEssayConfig>) => {
    onChange({ config: { ...item.config, ...patch } });
  };

  return (
    <div className="flex flex-col gap-[31px] w-full">
      <SectionHeader title="Essay" />
      <FloatingLabelPill
        label="Essay Question"
        value={prompt}
        onChange={(v) => updateConfig({ prompt: v })}
        placeholder="Does social media bring people closer together?"
        size="xl"
        readOnly={readOnly}
        trailingIcon={!readOnly && <TrashButton onClick={onDelete} ariaLabel="Delete question" />}
      />
      <p className="text-center text-xs text-text-disabled">
        Essay questions will have a text area for students to write their response.
      </p>
      <FloatingLabelPill
        label="Sample Answer (optional)"
        value={sample_answer ?? ''}
        onChange={(v) => updateConfig({ sample_answer: v })}
        placeholder="Add a sample response to guide grading…"
        readOnly={readOnly}
        multiline
        rows={6}
      />
      {!readOnly && (
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 font-sans text-xs text-text-secondary">
            Min words
            <input
              type="number"
              min={0}
              value={min_words ?? ''}
              onChange={(e) =>
                updateConfig({ min_words: e.target.value === '' ? undefined : Number(e.target.value) })
              }
              className={numberInputClass}
            />
          </label>
          <label className="flex flex-col gap-1 font-sans text-xs text-text-secondary">
            Max words
            <input
              type="number"
              min={1}
              value={max_words ?? ''}
              onChange={(e) =>
                updateConfig({ max_words: e.target.value === '' ? undefined : Number(e.target.value) })
              }
              className={numberInputClass}
            />
          </label>
        </div>
      )}
    </div>
  );
}
